/**
 * Layout classes for the stepper. Structure only — no visual decisions beyond
 * tokens.
 *
 * THE SHAPE, AND WHY IT IS THIS SHAPE
 * -----------------------------------
 * The previous version stacked the rail, the panel and the navigation in one
 * scrolling column. Two things went wrong with that:
 *
 *  1. The panel's height was whatever its content happened to be, so a long
 *     step (a content editor, a review read-back) grew the card past the
 *     viewport and the Continue button drifted off-screen. The operator had to
 *     scroll to find the control that moves them forward, which is the one
 *     control that should never be hunted for.
 *  2. On a short step the card collapsed to a stub, so the page height jumped
 *     between steps and the layout felt like it was being rebuilt each time.
 *
 * So the panel is now a FIXED-HEIGHT flex column: a header that names the step,
 * a body that scrolls, and a footer that is pinned. The card is the same height
 * on every step, the primary action is always in the same place, and long
 * content scrolls INSIDE the card rather than pushing the page around.
 *
 * The height is `min-h-0 flex-1` inside a `min-h-0` page, which lets the shell's
 * `<main>` decide the available height. Nothing here hard-codes a viewport
 * height, so the layout still works when the shell changes.
 *
 * `page` carries NO padding: the shell's `<main>` owns page gutters, so a
 * stepper cannot double them. See `app-list-layout` for the same rule.
 */
export const styles = {
  /**
   * The page frame.
   *
   * `min-h-0` is load-bearing: the card inside is a fixed-height panel whose body
   * scrolls, and a flex parent that refuses to shrink below its content would let
   * the card grow the page instead of scrolling inside itself.
   *
   * THE NEGATIVE BOTTOM MARGIN IS DELIBERATE, AND MOBILE-ONLY.
   * The shell's `<main>` reserves `pb-24` (6rem) so ordinary pages clear the
   * fixed bottom nav. A stepper is not an ordinary page: its card is a
   * fixed-height surface with its own pinned footer, so that 6rem of clearance
   * was not protecting anything — it was 96px of dead canvas under a card that
   * had already stopped growing, which is exactly the "lot of empty space" the
   * card was reported to have on a phone.
   *
   * `-mb-24` reclaims the whole reserve, and `pb-4` puts a normal page gutter
   * back so the card does NOT run flush to the bottom edge of the screen. The
   * net effect is `-mb-20` (5rem): the card gains 80px of height and still ends
   * 16px above the viewport bottom, matching the `px-4` side gutters.
   *
   * The nav clearance itself is re-applied INSIDE the card as padding on the
   * scrolling body, so the last field still clears the nav when the body is
   * scrolled to its end.
   *
   * It is released at `lg`, where the nav is `lg:hidden` and the shell already
   * drops its own `pb-24`.
   */
  page: 'flex min-h-0 flex-1 flex-col gap-4 -mb-24 pb-4 lg:mb-0 lg:pb-0',
  header: 'flex flex-wrap items-center justify-between gap-3',
  title: 'text-2xl font-semibold text-content',

  /**
   * The rail and the panel, side by side from `lg`.
   *
   * `min-h-0` is load-bearing: without it a flex child refuses to shrink below
   * its content, so the panel would grow the page instead of scrolling inside
   * itself — the exact bug this layout exists to fix.
   *
   * There is NO gap between the rail and the panel, and the two share one border
   * and one radius. They are one card with a tinted left column, not two cards
   * that happen to sit next to each other: the rail is the index of the form
   * beside it, and a gap made them read as unrelated panels the user had to
   * mentally join.
   *
   * THE MOBILE HEIGHT COMES FROM `flex-1`, NOT A FLOOR.
   * Below `lg` the rail is hidden, so the card's height is decided by the flex
   * chain: `page` is `flex-1` inside the shell's `<main>`, and the card is
   * `flex-1` inside `page`. That already makes the card fill the phone from just
   * under the heading down to the page gutter — no `min-h` needed.
   *
   * A `min-h` floor was tried first and was WRONG: it either fell short of the
   * available space (leaving the dead canvas the user reported) or overshot it
   * and forced a scroll on a short phone. The available height is not a constant
   * — it depends on the heading, the breadcrumbs and the shell's chrome — so any
   * hard-coded floor is a guess. `flex-1` measures it instead.
   *
   * `lg:min-h-0` is still required: a flex child defaults to `min-height: auto`
   * and would refuse to shrink below its content, so the panel would grow the
   * page instead of scrolling inside itself.
   */
  body:
    'flex flex-1 flex-col overflow-hidden rounded-surface border border-border bg-surface-raised lg:min-h-0 lg:flex-row',

  /**
   * The rail column.
   *
   * Hidden below `lg` because a vertical rail on a phone eats the width the form
   * needs and pushes the fields into a narrow strip. Mobile reaches the same list
   * through the step sheet instead (see `stepSheetTrigger`), which carries the
   * same information without spending height on it.
   *
   * It is the LEFT COLUMN OF THE SAME CARD as the panel, not a card of its own:
   * no border, no radius, and a tinted wash that separates it from the white form
   * beside it. A separate bordered card made the two look like unrelated panels;
   * a tinted column inside one card reads as "index, then content".
   *
   * THE TINT IS `accent-soft`, NOT `surface-sunken`.
   * The page canvas behind the card is ALREADY `surface-sunken` (the shell's
   * `<main>` owns it). Painting the rail the same token made the column vanish
   * into the page: the card's left edge disappeared and the rail read as a hole
   * cut out of the card rather than a column inside it. `accent-soft` is a
   * different hue as well as a different lightness, so the column is legible
   * against BOTH the white panel and the sunken canvas behind the card.
   *
   * `self-stretch` rather than `self-start`: the tint must run the full height of
   * the card, or the column ends partway down and the card looks broken.
   *
   * THE PADDING IS SYMMETRIC, AND THE ACTIVE ROW'S INSET IS TOO.
   * The active row paints `bg-surface-raised`, which is the SAME white as the form
   * panel on the other side of the rail's right border. With the row spanning the
   * full inner width, its background ran right up to that border, so the white of
   * the active row and the white of the panel met with only a 1px rule between
   * them — the row read as bleeding into the form, and the rail's right edge
   * stopped being legible as an edge at all.
   *
   * The first fix was to widen the rail's right padding (`p-3 pr-4`), which worked
   * but made the rail asymmetric: the active pill sat 20px from the rail's left
   * edge and 24px from its right, so the highlight looked off-centre in its own
   * column. The gutter belongs to the ROW, not the column — `railButton` now
   * carries `mx-1`, which insets every row equally on both sides and leaves the
   * rail's own padding symmetric.
   */
  rail:
    'hidden shrink-0 flex-col gap-1 self-stretch border-b border-border bg-accent-soft p-3 lg:flex lg:w-72 lg:border-b-0 lg:border-r',

  /**
   * The step list itself.
   *
   * A plain column that FILLS the rail, with none of the rail's own chrome. It
   * used to default to `styles.rail`, which meant the `<ol>` re-applied the
   * column's `lg:w-72`, `p-3`, `bg-accent-soft` and `border-r` INSIDE the column
   * that already carried them. The result was a 288px list inside a 288px parent
   * that had already spent 24px on padding, so the list overflowed the rail by
   * exactly that much and its tint and right border painted over the panel.
   *
   * The rail's chrome belongs to the rail. The list only has to stack its rows.
   */
  railList: 'flex flex-col gap-1',
  /**
   * One rail row.
   *
   * `flex flex-col` so the button inside can `self-stretch` to the full row width
   * while `mx-1` insets it symmetrically. A plain block `<li>` gives the button no
   * cross axis to stretch along, and a row-direction flex container stretches its
   * items VERTICALLY — so `self-stretch` collapsed the button to its content width
   * in both cases. A column container makes the cross axis horizontal, which is
   * the axis the button needs to fill.
   *
   * `relative` anchors the connector, which is absolutely positioned against this
   * element rather than the button — the button is inset by `mx-1`, and measuring
   * the line from the button would drag it 4px off the markers.
   */
  railItem: 'relative flex flex-col list-none',

  /**
   * One rail row: marker, then label and state.
   *
   * `items-start` rather than `items-center` so a two-line label keeps its
   * marker aligned with the first line instead of floating to the middle.
   *
   * `py-1.5` rather than `py-2`: with the connector line now running between
   * rows, the vertical rhythm comes from the line, so the rows themselves can sit
   * closer together without the rail reading as cramped.
   *
   * `mx-1` insets every row equally on both sides. The active row paints
   * `bg-surface-raised` — the same white as the form panel across the rail's right
   * border — so without an inset its highlight ran flush to that border and the
   * two whites met with only a 1px rule between them. The gutter lives here rather
   * than on the rail so the rail's own padding stays symmetric and the pill sits
   * the same distance from each edge of its column.
   *
   * `self-stretch`, NOT `w-full`. `w-full` is 100% of the parent's CONTENT box, so
   * adding `mx-1` on top of it made the row 8px wider than the space it had and
   * pushed it out past the rail's padding — the row overlapped the panel and the
   * two margins came out unequal (16px left, 9px right). `self-stretch` fills the
   * column's cross axis and then lets the margins inset it, which is what makes
   * the two sides match.
   *
   * The connector's `left-[1.375rem]` is measured from the `<li>`, which is NOT
   * inset, so `mx-1` does not move the line off the markers.
   */
  railButton:
    'mx-1 flex cursor-pointer items-start gap-3 self-stretch rounded-control px-2 py-1.5 text-left transition-colors hover:bg-surface-raised disabled:cursor-not-allowed disabled:opacity-60',
  railButtonCurrent: 'bg-surface-raised',

  /**
   * The numbered/checked marker.
   *
   * A circle that reads as a step number when pending, a tick when complete, and
   * a warning glyph when blocked. The glyph is `aria-hidden` — the state is
   * carried in text by `railState`, so the marker is decoration and must not be
   * announced twice.
   *
   * `relative z-10` lifts it above the connector line, which is absolutely
   * positioned behind the markers so the line appears to run from circle to
   * circle rather than stopping short of each one.
   */
  railMarker:
    'relative z-10 flex size-7 shrink-0 items-center justify-center rounded-full border text-xs font-medium',
  railMarkerPending: 'border-border-strong bg-surface-raised text-content-muted',
  railMarkerCurrent: 'border-accent bg-accent text-accent-content',
  railMarkerComplete: 'border-positive bg-positive-soft text-positive',
  railMarkerBlocked: 'border-critical bg-critical-soft text-critical',

  /** The label and its state line. `min-w-0` lets the label truncate, not overflow. */
  railText: 'flex min-w-0 flex-col gap-0.5 pt-0.5',
  railLabel: 'truncate text-sm',
  railLabelCurrent: 'font-medium text-content',
  railLabelIdle: 'text-content-muted',
  railState: 'text-xs',
  railStateComplete: 'text-positive',
  railStateCurrent: 'text-accent',
  railStateBlocked: 'text-critical',
  railStateIdle: 'text-content-subtle',

  /**
   * The connector between markers.
   *
   * A 1px rule that runs from under one marker to the next, so the rail reads as
   * a sequence rather than a list of unrelated rows. It is `aria-hidden` and
   * purely decorative — the `<ol>` already conveys order.
   *
   * It is absolutely positioned rather than a flex sibling so it can span the
   * full gap between two markers, including the row padding. As a flex sibling it
   * could only occupy the space between rows, which left a visible break at every
   * marker and made the rail read as four separate items instead of one journey.
   *
   * `left-[1.375rem]` centres it under the 1.75rem marker once the card's `p-3`
   * and the button's `px-2` are accounted for. The width is `w-[1px]` rather than
   * `w-px`: inside this flex column `w-px` resolved to zero, so the rule was
   * present in the DOM but invisible.
   */
  railConnector: 'absolute left-[1.375rem] top-7 bottom-0 w-[1px] bg-border-strong',

  /**
   * The help card at the foot of the rail.
   *
   * A quiet, always-present affordance rather than a link that appears only when
   * something goes wrong: an operator who is stuck should not have to fail first
   * to find out where to ask. It is deliberately NOT a button — there is nothing
   * to click yet — so it is styled as an informational card and says plainly that
   * the assistant is coming.
   *
   * `mt-auto` pushes it to the bottom of the column, so it sits at the foot of
   * the card rather than directly under the last step. `bg-surface-raised` lifts
   * it off the tinted rail so it reads as a distinct note, not as a fifth step.
   */
  helpCard:
    'mt-auto flex flex-col gap-1 rounded-control border border-border bg-surface-raised p-3',
  helpCardTitle: 'flex items-center gap-2 text-sm font-medium text-content',
  helpCardBody: 'text-xs text-content-muted',

  /**
   * The step sheet: the rail, as a bottom sheet on a phone.
   *
   * The sheet is anchored to the bottom edge and slides up, which is where a
   * thumb already is and where the platform's own sheets live. It is capped at
   * `max-h-[80dvh]` so the page behind stays partly visible — a sheet that fills
   * the screen is a page, and the user loses the sense that they can dismiss it.
   *
   * `rounded-t-surface` rounds only the top: the bottom edge is flush with the
   * screen, so rounding it would show the backdrop through the corners.
   */
  sheetBackdrop:
    'fixed inset-0 z-40 bg-content/40 transition-opacity duration-200 data-[starting-style]:opacity-0 data-[ending-style]:opacity-0',
  sheetViewport:
    'fixed inset-0 z-50 flex items-end justify-center transition-opacity duration-200 data-[starting-style]:opacity-0 data-[ending-style]:opacity-0',
  sheetPopup:
    'flex max-h-[80dvh] w-full flex-col overflow-hidden rounded-t-surface border-t border-border bg-surface-raised shadow-lg transition-transform duration-200 data-[starting-style]:translate-y-full data-[ending-style]:translate-y-full',
  sheetHeader:
    'flex shrink-0 items-center justify-between gap-3 border-b border-border px-4 py-3',
  sheetTitle: 'text-sm font-medium text-content',
  sheetClose:
    'inline-flex cursor-pointer items-center justify-center rounded-control p-1.5 text-content-muted transition-colors hover:bg-surface-sunken',
  sheetBody: 'min-h-0 flex-1 overflow-y-auto p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]',
  /**
   * The active step's description, at the top of the sheet.
   *
   * It sits above the rail rather than inside the active row so it reads as a
   * caption for the sheet — "this is what the step you are on is for" — instead
   * of as part of a list item. `border-b` separates it from the step list without
   * needing a second heading.
   */
  sheetDescription: 'mb-3 border-b border-border pb-3 text-sm text-content-muted',
  sheetRail: 'flex flex-col gap-1',

  /**
   * The panel: the right column of the shared card, with a scrolling body and a
   * pinned footer.
   *
   * It carries NO border and NO radius of its own. The card that wraps the rail
   * and the panel owns both, so the two columns share one outline instead of
   * drawing two that nearly touch. `overflow-hidden` clips the body's scroll to
   * the card's rounded corners, so a long step does not paint over the radius.
   */
  panel: 'flex min-h-0 flex-1 flex-col overflow-hidden bg-surface-raised',

  /**
   * The step's name and description. Does not scroll — it is the card's heading.
   *
   * `py-3` rather than `py-4`: the header holds one line of text and a 36px
   * control, so 16px of padding above and below it was 32px of the card's height
   * spent on air. The heading is a caption for the form below it, not a section
   * of its own, and on a phone every pixel here comes straight out of the
   * scrolling body — which is where the fields the user actually came for live.
   *
   * The horizontal padding stays `px-6` so the heading keeps its alignment with
   * the fields in the body.
   */
  panelHeader: 'flex flex-col gap-1 border-b border-border px-6 py-3',
  panelHeaderRow: 'flex items-center justify-between gap-3',
  panelTitle: 'text-lg font-medium text-content',
  /**
   * The step's description.
   *
   * `hidden lg:block` because on a phone it moves into the step sheet. A
   * permanent description under the heading wrapped to three or four lines and
   * pushed the first field of every step below the fold — a cost paid on every
   * step for a paragraph the user reads on the first one. The sheet carries it
   * for the active step instead, one tap away and costing the form no height.
   *
   * It is hidden rather than removed so the wide layout is unchanged: from `lg`
   * the rail is on screen and there is room for the description in the panel.
   */
  stepDescription: 'hidden text-sm text-content-muted lg:block',
  /**
   * The phone-only control that opens the step sheet.
   *
   * It shows the position — "Step 1/3" — rather than an icon alone. The count is
   * the most useful fact about a multi-step form and the one the phone layout
   * otherwise hides, because the rail that carries it is `hidden` below `lg`.
   * The label is the affordance; the icon beside it says what the tap will do.
   *
   * `min-h-9` rather than a fixed `size-9`: the control now has text, so it must
   * grow with the label instead of clipping it. The height still meets the 44px
   * touch target once the border is counted, and `px-3` gives the text room.
   *
   * Hidden from `lg`, where the rail is already on screen and a second way to
   * reach the same list would be redundant.
   */
  stepSheetTrigger:
    'inline-flex min-h-9 shrink-0 cursor-pointer items-center gap-2 rounded-control border border-border bg-surface-raised px-3 text-xs font-medium text-content-muted transition-colors hover:border-border-strong hover:text-content lg:hidden',

  /**
   * The scrolling region.
   *
   * `min-h-0` again: a flex child defaults to `min-height: auto`, which means it
   * grows to fit its content and never scrolls. This is the single most common
   * reason a "scrolling" flex panel does not scroll.
   *
   * `pb-24 lg:pb-5` pairs with the page's `-mb-24`: the clearance the shell gave
   * up is re-applied here, inside the card, so the last field still clears the
   * fixed bottom nav when the body is scrolled to its end. On `lg` the nav is
   * gone and the padding returns to the normal `py-5` rhythm.
   *
   * `pt-4` rather than `pt-5`: the header above already ends in a border, so the
   * body does not need a second full gutter to separate itself from it.
   */
  panelBody: 'min-h-0 flex-1 overflow-y-auto px-6 pt-4 pb-24 lg:pb-5',
  /**
   * The pinned footer.
   *
   * `shrink-0` keeps it at its natural height while the body above absorbs the
   * leftover space, so the primary action is always visible without scrolling.
   *
   * `py-3` rather than `py-4`, for the same reason as the header: the footer
   * holds a single row of 36px controls, so 16px of padding above and below was
   * 32px of the card's height spent on air. The buttons carry their own height
   * and the border above already separates the footer from the body, so the
   * padding only needs to keep the controls off the edges.
   */
  panelFooter:
    'flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-border bg-surface-raised px-6 py-3',

  /** The submit error sits above the footer, inside the card, so it is not missed. */
  footerError: 'border-t border-border px-6 py-3',

  nav: 'flex items-center gap-3',

  /**
   * An optional third column for supporting content.
   *
   * `lg:w-80` and `shrink-0` so it holds its width while the panel absorbs the
   * leftover space — the panel is the thing that should flex, not the aside.
   */
  aside: 'flex w-full shrink-0 flex-col gap-4 lg:w-80',
} as const;
